import { useEffect, useRef, type ReactNode } from 'react';

/**
 * The add/edit dialog.
 *
 * A native <dialog>, so the focus trap, the backdrop and Escape are the
 * browser's job. What is *not* the browser's job — at least not reliably — is
 * telling us it closed: `dialog.close()` does not fire a `close` event in
 * every engine, and the old console lost an afternoon to exactly that. So
 * closing is driven by the three things a person can actually do, and the
 * `close` event is treated as a bonus rather than the mechanism.
 *
 * React then unmounts this component, which is what removes the element — no
 * reconciling, no moving nodes around, because the form lives here rather than
 * somewhere else on the page.
 */
export function Modal({ title, onClose, children, footer }: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  /* The latest onClose without re-running the open effect, which would close
     and reopen the dialog on every parent render. */
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (!dlg.open) dlg.showModal();

    // Escape: `cancel` fires reliably, `close` does not.
    const onCancel = (e: Event) => { e.preventDefault(); close.current(); };
    const onCloseEvent = () => close.current();
    dlg.addEventListener('cancel', onCancel);
    dlg.addEventListener('close', onCloseEvent);
    return () => {
      dlg.removeEventListener('cancel', onCancel);
      dlg.removeEventListener('close', onCloseEvent);
      if (dlg.open) dlg.close();
    };
  }, []);

  return (
    <dialog
      ref={ref}
      className="mdl"
      /* A click on the backdrop lands on the dialog element itself; a click on
         anything inside lands on a child. */
      onClick={(e) => { if (e.target === ref.current) onClose(); }}
    >
      <div className="mdl-panel">
        <div className="mdl-head">
          <span className="mdl-title">{title}</span>
          <button type="button" className="mdl-x" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="mdl-body">{children}</div>
        {footer ? <div className="mdl-foot">{footer}</div> : null}
      </div>
    </dialog>
  );
}
